import { getToday } from "../../../shared/types/date-helpers";

export interface GamificationState {
  readonly xp: number;
  readonly level: number;
  readonly achievements: readonly string[];
  readonly lastXpDate: string;
  readonly tasksCompleted: number;
  readonly habitsCompleted: number;
  readonly journalEntries: number;
  readonly loginStreak: number;
  readonly lastLoginDate: string;
}

const STORAGE_KEY = "palnia_gamification";

const INITIAL_STATE: GamificationState = {
  xp: 0,
  level: 0,
  achievements: [],
  lastXpDate: getToday(),
  tasksCompleted: 0,
  habitsCompleted: 0,
  journalEntries: 0,
  loginStreak: 0,
  lastLoginDate: getToday(),
};

function loadState(): GamificationState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return INITIAL_STATE;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed === "object" && parsed !== null) {
      const partial = parsed as Record<string, unknown>;
      return {
        ...INITIAL_STATE,
        ...(Object.fromEntries(
          Object.entries(partial).filter(([k]) => k in INITIAL_STATE),
        ) as Partial<GamificationState>),
      };
    }
    return INITIAL_STATE;
  } catch {
    return INITIAL_STATE;
  }
}

let stateCache: GamificationState = loadState();
const listeners = new Set<() => void>();

type AchievementUnlockedCallback = (achievementId: string) => void;
const achievementUnlockedListeners = new Set<AchievementUnlockedCallback>();

export function onAchievementUnlocked(cb: AchievementUnlockedCallback): () => void {
  achievementUnlockedListeners.add(cb);
  return () => { achievementUnlockedListeners.delete(cb); };
}

export function notifyAchievementUnlocked(id: string): void {
  for (const cb of achievementUnlockedListeners) {
    try { cb(id); } catch { /* ignore */ }
  }
}

function emitChange() {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(stateCache));
  for (const listener of listeners) {
    try {
      listener();
    } catch {
      /* ignore stale listener */
    }
  }
}

export function subscribe(listener: () => void) {
  listeners.add(listener);
  function handleStorageChange(event: StorageEvent) {
    if (event.key === STORAGE_KEY) {
      stateCache = loadState();
      emitChange();
    }
  }
  window.addEventListener("storage", handleStorageChange);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", handleStorageChange);
  };
}

export function getSnapshot(): GamificationState {
  return stateCache;
}

export function addXp(amount: number, reason: string): void {
  const today = getToday();
  const canAddXp = stateCache.lastXpDate !== today || reason === "login_streak";

  if (!canAddXp && reason !== "login_streak") return;

  const newXp = stateCache.xp + amount;
  const newLevel = Math.floor(Math.sqrt(newXp / 50));

  stateCache = {
    ...stateCache,
    xp: newXp,
    level: newLevel,
    lastXpDate: today,
  };

  emitChange();
}

export function checkAchievements(): void {
  runAchievementCheck();
}

function runAchievementCheck(): void {
  const currentAchievements = new Set(stateCache.achievements);
  const newlyUnlocked: string[] = [];

  const checks: Array<[string, boolean]> = [
    ["first_task", stateCache.tasksCompleted >= 1],
    ["ten_tasks", stateCache.tasksCompleted >= 10],
    ["fifty_tasks", stateCache.tasksCompleted >= 50],
    ["streak_7", stateCache.loginStreak >= 7],
    ["streak_30", stateCache.loginStreak >= 30],
    ["habit_master", stateCache.habitsCompleted >= 100],
  ];

  for (const [id, condition] of checks) {
    if (condition && !currentAchievements.has(id)) {
      currentAchievements.add(id);
      newlyUnlocked.push(id);
    }
  }

  if (newlyUnlocked.length > 0) {
    stateCache = {
      ...stateCache,
      achievements: Array.from(currentAchievements),
    };
    emitChange();
    for (const id of newlyUnlocked) notifyAchievementUnlocked(id);
  }
}

export function checkTimeAchievement(): void {
  const hour = new Date().getHours();
  const currentAchievements = new Set(stateCache.achievements);
  const newlyUnlocked: string[] = [];

  if (hour < 8 && !currentAchievements.has("early_bird")) {
    currentAchievements.add("early_bird");
    newlyUnlocked.push("early_bird");
  }
  if (hour >= 22 && !currentAchievements.has("night_owl")) {
    currentAchievements.add("night_owl");
    newlyUnlocked.push("night_owl");
  }

  if (newlyUnlocked.length > 0) {
    stateCache = {
      ...stateCache,
      achievements: Array.from(currentAchievements),
    };
    emitChange();
    for (const id of newlyUnlocked) notifyAchievementUnlocked(id);
  }
}

export function getNextLevelXp(xp: number): number {
  const currentLevel = Math.floor(Math.sqrt(xp / 50));
  const nextLevel = currentLevel + 1;
  return nextLevel * nextLevel * 50;
}

export function getProgressPercent(xp: number): number {
  const currentLevel = Math.floor(Math.sqrt(xp / 50));
  const currentLevelXp = currentLevel * currentLevel * 50;
  const nextLevelXp = (currentLevel + 1) * (currentLevel + 1) * 50;
  const progress = xp - currentLevelXp;
  const needed = nextLevelXp - currentLevelXp;
  return Math.min(100, Math.floor((progress / needed) * 100));
}

export function incrementTaskCompleted(): void {
  stateCache = {
    ...stateCache,
    tasksCompleted: stateCache.tasksCompleted + 1,
  };
  emitChange();
  runAchievementCheck();
}

export function incrementHabitCompleted(): void {
  stateCache = {
    ...stateCache,
    habitsCompleted: stateCache.habitsCompleted + 1,
  };
  emitChange();
  runAchievementCheck();
}

export function incrementJournalEntries(): void {
  stateCache = {
    ...stateCache,
    journalEntries: stateCache.journalEntries + 1,
  };
  emitChange();
}

export function updateLoginStreak(streak: number): void {
  stateCache = {
    ...stateCache,
    loginStreak: streak,
    lastLoginDate: getToday(),
  };
  emitChange();
  runAchievementCheck();
}

export function trackLogin(): void {
  const today = getToday();
  if (stateCache.lastLoginDate === today) return;

  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayStr = yesterday.toISOString().slice(0, 10);

  const newStreak = stateCache.lastLoginDate === yesterdayStr
    ? stateCache.loginStreak + 1
    : 1;

  updateLoginStreak(newStreak);
  addXp(10, "login_streak");
}
